import React from 'react';
import styled from 'styled-components';
import { RightOutlined } from '@ant-design/icons';
import { ActivityItem } from './types';
import { useMessages } from '../../i18n';

const ko = { viewAll: '전체보기', noContent: '내용 없음' };
const en: typeof ko = { viewAll: 'View all', noContent: 'No content' };

interface ActivityWidgetProps {
  title: React.ReactNode;
  items: ActivityItem[];
  emptyMessage: string;
  emptySubMessage: string;
  onViewAll?: () => void;
  onItemClick?: (item: ActivityItem) => void;
}

const ActivityWidget = ({ title, items, emptyMessage, emptySubMessage, onViewAll, onItemClick }: ActivityWidgetProps) => {
  const t = useMessages({ ko, en });
  return (
    <DashboardCard>
      <WidgetHeader>
        <SectionTitle>{title}</SectionTitle>
        {onViewAll && items.length > 0 && (
          <ViewAllButton onClick={onViewAll}>
            {t.viewAll} <RightOutlined className="icon" />
          </ViewAllButton>
        )}
      </WidgetHeader>
      
      {items.length > 0 ? (
        <ListContainer>
          {items.map((item, idx) => (
            <ListItem key={item.id || idx}>
              <div onClick={() => onItemClick && onItemClick(item)} className="item-title">{item.title || t.noContent}</div>
              <div className="item-date">{item.date}</div>
            </ListItem>
          ))}
        </ListContainer>
      ) : (
        <EmptyState>
          {emptyMessage}<br />
          <span className="sub">{emptySubMessage}</span>
        </EmptyState>
      )}
    </DashboardCard>
  );
};

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ViewAllButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  .icon {
    font-size: 10px;
  }

  &:hover {
    color: #17375f;
    background: #f0f0f0;
  }
`;

const DashboardCard = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #17375f;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .item-title {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 70%;
    cursor: pointer;

    &:hover {
      color: #17375f;
      text-decoration: underline;
    }
  }

  .item-date {
    font-size: 12px;
    color: #888;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
  border: 1px dashed #d9d9d9;
  color: #888;
  font-size: 15px;
  line-height: 1.6;

  .sub {
    font-size: 13px;
    color: #bbb;
  }
`;

export default ActivityWidget;